/**
 * Builds self-contained HTML for a ReactFlow diagram rendered in an iframe.
 * Used by page.tsx for rendering ReactFlow diagrams.
 * @param mode - 'light' or 'dark' to match the parent app theme
 */
export function buildReactFlowHtml(jsonData: string, mode: 'light' | 'dark' = 'light'): string {
  const isDark = mode === 'dark';
  const t = {
    bg: isDark ? '#121212' : '#f7f7f8',
    nodeBg: isDark ? '#1e1e1e' : '#fff',
    nodeBorder: isDark ? '#333' : '#CDD7E1',
    nodeHoverBorder: isDark ? '#5B9CF5' : '#0B6BCB',
    nodeHoverShadow: isDark ? '0 4px 20px 0 rgba(0,0,0,0.5)' : '0 4px 20px 0 rgba(34,41,47,0.14)',
    nodeShadow: isDark ? '0 2px 12px 0 rgba(0,0,0,0.3)' : '0 2px 12px 0 rgba(34,41,47,0.08)',
    labelColor: isDark ? '#e0e0e0' : '#0B0D0E',
    descColor: isDark ? '#9e9e9e' : '#5A6169',
    edgePillBg: isDark ? '#2a2a2a' : 'white',
    edgePillBorder: isDark ? '#444' : '#CDD7E1',
    edgePillColor: isDark ? '#bbb' : '#5A6169',
    edgeStroke: isDark ? '#444' : '#CDD7E1',
    handleBg: isDark ? '#1e1e1e' : '#fff',
    handleBorder: isDark ? '#555' : '#CDD7E1',
    controlsBorder: isDark ? '#333' : '#CDD7E1',
    controlsShadow: isDark ? '0 2px 12px 0 rgba(0,0,0,0.4)' : '0 2px 12px 0 rgba(34,41,47,0.08)',
    controlsBg: isDark ? '#1e1e1e' : '#fff',
    controlsColor: isDark ? '#ccc' : '#333',
    gridColor: isDark ? '#333' : '#CDD7E1',
    minimapBg: isDark ? '#1a1a1a' : undefined,
  };
  return `<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<script src="https://unpkg.com/react@18/umd/react.production.min.js"><\/script>
<script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"><\/script>
<script src="https://unpkg.com/@babel/standalone/babel.min.js"><\/script>
<script src="https://unpkg.com/reactflow@11.11.4/dist/umd/index.js"><\/script>
<script src="https://unpkg.com/dagre@0.8.5/dist/dagre.min.js"><\/script>
<link href="https://unpkg.com/reactflow@11.11.4/dist/style.css" rel="stylesheet">
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Poppins', sans-serif; background: ${t.bg}; }
  #root { width: 100vw; height: 100vh; }
  .react-flow__node { font-family: 'Poppins', sans-serif; }
  .react-flow__controls { border-radius: 10px; border: 1px solid ${t.controlsBorder}; box-shadow: ${t.controlsShadow}; background: ${t.controlsBg}; }
  .react-flow__controls-button { border-bottom: 1px solid ${t.controlsBorder}; background: ${t.controlsBg}; }
  .react-flow__controls-button svg { fill: ${t.controlsColor}; }
  .react-flow__minimap { border-radius: 10px; border: 1px solid ${t.controlsBorder}; box-shadow: ${t.controlsShadow}; ${t.minimapBg ? `background: ${t.minimapBg};` : ''} }
  @keyframes dashflow { to { stroke-dashoffset: -20; } }
  .animated-edge { stroke-dasharray: 5 5; animation: dashflow 0.8s linear infinite; }
  .edge-label-pill {
    background: ${t.edgePillBg}; border: 1px solid ${t.edgePillBorder}; border-radius: 6px; padding: 2px 8px;
    font-size: 10px; font-weight: 500; color: ${t.edgePillColor}; font-family: 'Poppins', sans-serif;
    box-shadow: 0 1px 4px rgba(${isDark ? '0,0,0,0.3' : '34,41,47,0.06'});
  }
  .custom-node {
    background: ${t.nodeBg}; border: 1px solid ${t.nodeBorder}; border-radius: 12px;
    box-shadow: ${t.nodeShadow}; min-width: 180px; max-width: 280px;
    transition: box-shadow 0.2s, border-color 0.2s;
  }
  .custom-node:hover { box-shadow: ${t.nodeHoverShadow}; border-color: ${t.nodeHoverBorder}; }
  .custom-node .accent { width: 4px; position: absolute; top: 0; bottom: 0; left: 0; border-radius: 12px 0 0 12px; }
  .custom-node .body { padding: 12px 14px 12px 20px; display: flex; align-items: flex-start; gap: 10px; }
  .custom-node .icon-wrap {
    width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .custom-node .info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .custom-node .label { font-size: 13px; font-weight: 600; color: ${t.labelColor}; line-height: 1.3; }
  .custom-node .desc { font-size: 11px; color: ${t.descColor}; line-height: 1.3; white-space: normal; word-break: break-word; }
  .custom-node .status { display: flex; align-items: center; gap: 4px; margin-top: 4px; }
  .custom-node .status-dot { width: 6px; height: 6px; border-radius: 50%; }
  .custom-node .status-text { font-size: 10px; font-weight: 500; }
  .react-flow__handle {
    width: 8px; height: 8px; border: 2px solid ${t.handleBorder}; background: ${t.handleBg}; border-radius: 50%;
    transition: border-color 0.2s;
  }
  .react-flow__handle:hover { border-color: ${t.nodeHoverBorder}; }
</style>
</head><body>
<div id="root"></div>
<script type="text/babel">
const { ReactFlow: RF, Controls, MiniMap, Background, Handle, Position, MarkerType, getBezierPath, useNodesState, useEdgesState } = window.ReactFlow || window.reactflow;
const dagre = window.dagre;

const GROUP_COLORS = {
  service: '#0B6BCB', database: '#4CAF50', external: '#FF9800', client: '#9C27B0',
  queue: '#00BCD4', storage: '#8BC34A', compute: '#F44336', default: '#0B6BCB'
};

const STATUS_COLORS = { green: '#4CAF50', yellow: '#FF9800', red: '#F44336', blue: '#0B6BCB', gray: '#9DA0A6' };

// Threat status colors for threat model diagram
const THREAT_STATUS_COLORS = {
  POTENTIAL: '#ef4444',  // Red - active threat
  SAFE: '#22c55e',       // Green - no threat  
  MITIGATED: '#f59e0b',  // Orange - mitigated
  default: '#CDD7E1'
};

// Compliance status colors for compliance diagram
const COMPLIANCE_STATUS_COLORS = {
  COMPLIANT: '#22c55e',      // Green - compliant
  NON_COMPLIANT: '#ef4444',  // Red - non-compliant
  PARTIAL: '#f59e0b',        // Orange - partial compliance
  default: '#CDD7E1'
};

function getIcon(name, size, color) {
  try {
    if (window.lucideReact && window.lucideReact[name]) {
      const Icon = window.lucideReact[name];
      return React.createElement(Icon, { size: size || 18, color: color || '#fff' });
    }
  } catch {}
  return React.createElement('div', { style: { width: size || 18, height: size || 18, borderRadius: 4, background: color || '#fff', opacity: 0.5 } });
}

function CustomNode({ data }) {
  const group = data.group || 'default';
  const color = GROUP_COLORS[group] || GROUP_COLORS.default;
  
  // Get threat status for color coding (supports threatStatus or riskLevel)
  const threatStatus = data.threatStatus || data.riskLevel;
  const threatColor = threatStatus ? (THREAT_STATUS_COLORS[threatStatus] || THREAT_STATUS_COLORS.default) : null;
  
  // Get compliance status for color coding
  const complianceStatus = data.complianceStatus;
  const complianceColor = complianceStatus ? (COMPLIANCE_STATUS_COLORS[complianceStatus] || COMPLIANCE_STATUS_COLORS.default) : null;
  
  // Use compliance color if present, otherwise threat color
  const statusColor = complianceColor || threatColor;
  const borderColor = statusColor || '${t.nodeBorder}';
  const borderWidth = (threatStatus || complianceStatus) ? '3px' : '1px';
  const boxShadow = threatStatus === 'POTENTIAL' || complianceStatus === 'NON_COMPLIANT' ? '0 0 16px rgba(239, 68, 68, 0.4)' :
                    threatStatus === 'SAFE' || complianceStatus === 'COMPLIANT' ? '0 0 12px rgba(34, 197, 94, 0.3)' :
                    threatStatus === 'MITIGATED' || complianceStatus === 'PARTIAL' ? '0 0 12px rgba(245, 158, 11, 0.3)' :
                    '${t.nodeShadow}';
  
  // Use status color for accent if available
  const accentColor = statusColor || color;
  
  return React.createElement('div', { 
    className: 'custom-node', 
    title: data.description || undefined,
    style: { 
      position: 'relative',
      borderColor: borderColor,
      borderWidth: borderWidth,
      boxShadow: boxShadow
    } 
  },
    React.createElement(Handle, { type: 'target', position: Position.Top, style: { top: -4 } }),
    React.createElement(Handle, { type: 'target', position: Position.Left, style: { left: -4 } }),
    React.createElement('div', { className: 'accent', style: { background: accentColor } }),
    React.createElement('div', { className: 'body' },
      React.createElement('div', { className: 'icon-wrap', style: { background: accentColor + '18' } },
        getIcon(data.icon || 'Box', 18, accentColor)
      ),
      React.createElement('div', { className: 'info' },
        React.createElement('div', { className: 'label' }, data.label),
        data.description && React.createElement('div', { className: 'desc' }, data.description),
        // Show compliance status badge if present
        complianceStatus && React.createElement('div', { className: 'status' },
          React.createElement('div', { className: 'status-dot', style: { background: complianceColor } }),
          React.createElement('span', { className: 'status-text', style: { color: complianceColor } }, 
            complianceStatus === 'COMPLIANT' ? 'Compliant' : 
            complianceStatus === 'NON_COMPLIANT' ? 'Non-Compliant' : 
            complianceStatus === 'PARTIAL' ? 'Partial' : complianceStatus
          )
        ),
        // Show threat status badge if present and no compliance status
        !complianceStatus && threatStatus && React.createElement('div', { className: 'status' },
          React.createElement('div', { className: 'status-dot', style: { background: threatColor } }),
          React.createElement('span', { className: 'status-text', style: { color: threatColor } }, 
            threatStatus === 'POTENTIAL' ? 'Potential Threat' : 
            threatStatus === 'SAFE' ? 'Safe' : 
            threatStatus === 'MITIGATED' ? 'Mitigated' : threatStatus
          )
        ),
        // Also show regular status if present and no threat/compliance status
        !complianceStatus && !threatStatus && data.status && React.createElement('div', { className: 'status' },
          React.createElement('div', { className: 'status-dot', style: { background: STATUS_COLORS[data.status.color] || STATUS_COLORS.gray } }),
          React.createElement('span', { className: 'status-text', style: { color: STATUS_COLORS[data.status.color] || STATUS_COLORS.gray } }, data.status.label)
        )
      )
    ),
    React.createElement(Handle, { type: 'source', position: Position.Bottom, style: { bottom: -4 } }),
    React.createElement(Handle, { type: 'source', position: Position.Right, style: { right: -4 } })
  );
}

function AnimatedEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, markerEnd, style }) {
  const [path, labelX, labelY] = getBezierPath({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition });
  return React.createElement('g', null,
    React.createElement('defs', null,
      React.createElement('linearGradient', { id: 'grad-' + id, x1: '0%', y1: '0%', x2: '100%', y2: '100%' },
        React.createElement('stop', { offset: '0%', stopColor: '#0B6BCB', stopOpacity: 0.8 }),
        React.createElement('stop', { offset: '100%', stopColor: '#42A5F5', stopOpacity: 0.8 })
      )
    ),
    React.createElement('path', { d: path, fill: 'none', stroke: '${t.edgeStroke}', strokeWidth: 2 }),
    React.createElement('path', {
      d: path, fill: 'none', stroke: 'url(#grad-' + id + ')', strokeWidth: 2,
      className: 'animated-edge', markerEnd: markerEnd
    }),
    data?.label && React.createElement('foreignObject', {
      x: labelX - 40, y: labelY - 10, width: 80, height: 20,
      style: { overflow: 'visible', pointerEvents: 'none' }
    },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'center' } },
        React.createElement('span', { className: 'edge-label-pill' }, data.label)
      )
    )
  );
}

const nodeTypes = { custom: CustomNode };
const edgeTypes = { animated: AnimatedEdge };

function autoLayout(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 80, ranksep: 120, marginx: 40, marginy: 40 });
  nodes.forEach(n => g.setNode(n.id, { width: 200, height: 80 }));
  edges.forEach(e => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map(n => {
    const pos = g.node(n.id);
    return { ...n, position: { x: pos.x - 100, y: pos.y - 40 }, type: 'custom' };
  });
}

const inputData = ${jsonData};
const rawNodes = (inputData.nodes || []).map(n => ({ ...n, type: 'custom', position: n.position || { x: 0, y: 0 } }));
const rawEdges = (inputData.edges || []).map(e => ({
  ...e,
  type: 'animated',
  markerEnd: { type: MarkerType.ArrowClosed, color: '#0B6BCB', width: 16, height: 16 },
  data: { label: e.label }
}));
const layoutNodes = autoLayout(rawNodes, rawEdges);

function App() {
  const [nodes, setNodes, onNodesChange] = useNodesState(layoutNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(rawEdges);
  return React.createElement(RF, {
    nodes, edges, onNodesChange, onEdgesChange, nodeTypes, edgeTypes,
    fitView: true, fitViewOptions: { padding: 0.2 },
    minZoom: 0.2, maxZoom: 2, proOptions: { hideAttribution: true },
    style: { width: '100%', height: '100%' }
  },
    React.createElement(Controls, { showInteractive: false }),
    React.createElement(MiniMap, {
      nodeColor: (n) => {
        const cs = n.data?.complianceStatus;
        if (cs) return COMPLIANCE_STATUS_COLORS[cs] || '#0B6BCB';
        const ts = n.data?.threatStatus || n.data?.riskLevel;
        if (ts) return THREAT_STATUS_COLORS[ts] || '#0B6BCB';
        return GROUP_COLORS[n.data?.group] || '#0B6BCB';
      },
      style: { width: 120, height: 80 }
    }),
    React.createElement(Background, { color: '${t.gridColor}', gap: 20, size: 1 })
  );
}
ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(App));

window.addEventListener('load', () => {
  setTimeout(() => {
    window.parent.postMessage({ type: 'reactflow-resize', height: document.body.scrollHeight }, '*');
  }, 500);
});
<\/script>
</body></html>`;
}
